import { useMemo } from 'react';
import { RowConfig, ColumnConfig, SortConfig, FilterConfig } from '@/shared/types/table';
import { sortRows } from '../utils/sorting';
import { filterRows, searchRows } from '../utils/filtering';

interface UseTableDataProps<T> {
  rows: RowConfig<T>[];
  columns: ColumnConfig<T>[];
  sortConfig?: SortConfig;
  filters?: FilterConfig[];
  searchQuery?: string;
  currentPage?: number;
  pageSize?: number;
}

export const useTableData = <T>({
  rows,
  columns,
  sortConfig,
  filters = [],
  searchQuery = '',
  currentPage = 1,
  pageSize,
}: UseTableDataProps<T>) => {
  const processedRows = useMemo(() => {
    let result = [...rows];

    if (searchQuery) {
      result = searchRows(result, searchQuery, columns);
    }

    if (filters.length > 0) {
      result = filterRows(result, filters, columns);
    }

    if (sortConfig) {
      result = sortRows(result, sortConfig.columnId, sortConfig.direction, columns);
    }

    return result;
  }, [rows, columns, sortConfig, filters, searchQuery]);

  const paginatedRows = useMemo(() => {
    if (!pageSize) return processedRows;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return processedRows.slice(startIndex, endIndex);
  }, [processedRows, currentPage, pageSize]);

  return {
    processedRows,
    paginatedRows,
    totalRows: processedRows.length,
  };
};
